// Payment initializer of the aldoo.com flow: store {checkout_payment} creates
// (or reuses) the amount-based PaymentIntent for the whole cart, groups it by
// seller, and reserves the stock. The framework calls it when the checkout
// opens for a known buyer and again on the payment step (refreshPayment),
// once the address / shipping / invoice details are known.
import { getValue } from "aldoo-ra/Value"
import { CheckoutPayment, ReleaseReservation, getDeviceID } from "../storeApi"
import { getUTM } from "./utm"
import { hasPhysicalItems, toCheckoutCart } from "./cartItems"
import { cartCurrency } from "../store/currency"

// "Need an invoice?" on the payment step, kept in the flow's step data: it is
// cleared when the checkout closes, so a request applies to this checkout only
export const INVOICE_REQUEST = "invoice-requested"
// every parcel collected at its seller's own shop (pickup from the shop): cash on delivery is paid there —
// set by the delivery step, read by the payment step's choice, its total bar and the thank-you
export const PAY_AT_SHOP_KEY = "sf-checkout-pay-at-shop"

const shippingCents = (delivery) => Math.round(((delivery?.shipping?.totalCost || 0) * 100))

// every parcel collected at an office or a locker (the address step asked only who collects): one way
// for the cart, or — sellers with no courier in common — each seller's
export const allPickup = (delivery) => (delivery?.perSeller ? (delivery.deliveries || []).length > 0 && delivery.deliveries.every((entry) => entry.option?.pickup) : !!delivery?.option?.pickup)

// The last payment created for a given payload: the payment step re-runs the
// initializer (refreshPayment) and must not touch Stripe when nothing changed
let lastPayment = null

export async function initializePayment({ store, cart, getData, auth }) {
  const email = auth?.email || store.purchaseUserEmail
  if (!email) return { error: "email_required" }
  const billing = getData("billing")
  const delivery = getData("delivery")
  const invoice = getData("invoice-details")
  const language = getValue("current-site-language", "current-site-language") || "en"
  const deviceID = await getDeviceID()
  // for an office / locker order the address step gave who collects it
  // (name, phone) and the office's town; the office itself goes as `delivery`
  const address = billing?.address || null
  // the checkout just opened on a parcel: no address yet, nothing to ask the API (it would
  // answer invalid_address) — the payment step makes the payment once the address is known
  // (no delivery chosen either: on the payment step a missing address is still the API's error to show)
  if (!address && !delivery?.option && !delivery?.perSeller && hasPhysicalItems(cart)) return { skipped: true }

  const payload = {
    cart: toCheckoutCart(cart),
    // what the cart is priced in; the API converts every line into it
    currency: cartCurrency(),
    email,
    deviceID,
    billingAddress: address,
    shippingAddress: address ? { ...address } : null,
    shippingCost: shippingCents(delivery),
    // the way it travels, as the delivery step chose it; the API prices it again
    delivery: delivery?.option ? { providerId: delivery.option.provider, method: delivery.option.method, officeId: delivery.option.office?.id || null } : null,
    // each seller's own way, when they share no courier
    ...(delivery?.perSeller ? { deliveries: (delivery.deliveries || []).map((entry) => ({ sellerId: entry.sellerId, providerId: entry.option.provider, method: entry.option.method, officeId: entry.option.office?.id || null })) } : {}),
    invoiceDetails: invoice?.details || null,
    language,
    utm: getUTM(),
    // the code the buyer applied in the cart; the API resolves it against
    // Stripe and takes the discount off the total itself
    promoCode: store.promoCode || null,
  }

  const signature = JSON.stringify(payload)
  // a reservation that ran out is made again (ReservationToast's "Restore now"), whatever else is the same
  const held = !lastPayment?.paymentInfo?.reservationExpiresAt || new Date(lastPayment.paymentInfo.reservationExpiresAt).getTime() > Date.now()
  if (held && lastPayment?.signature === signature && lastPayment.paymentInfo?.paymentIntentId === store.paymentInfo?.paymentIntentId) {
    return lastPayment.paymentInfo
  }

  const response = await CheckoutPayment(payload)
  if (response?.error) return response

  const cents = response.amount || 0
  const paymentInfo = {
    requiresPayment: true,
    type: "purchase",
    pid: cart[0]?.pid,
    clientSecret: response.clientSecret,
    paymentIntentId: response.paymentIntentId,
    amount: cents,
    total: cents / 100,
    currency: (response.currency || payload.currency).toLowerCase(),
    // for the purchase event: the delivery in it (major units), the buyer's phone (Google Ads enhanced conversions)
    shipping: (payload.shippingCost || 0) / 100,
    phone: address?.phone || null,
    reservationExpiresAt: response.reservationExpiresAt || null,
    ttlSeconds: response.ttlSeconds || null,
  }
  lastPayment = { signature, paymentInfo }
  return paymentInfo
}

/** Abandoned checkout: free the reserved stock of its PaymentIntent */
export function releaseReservation(paymentInfo) {
  const id = paymentInfo?.paymentIntentId
  if (!id) return
  ReleaseReservation(id).catch(() => {})
}

/** Stripe confirmParams: hand Stripe the address collected by the billing step */
export function getConfirmParams({ store, email }) {
  const stepData = getValue("purchase-flow-step-data") || {}
  // an office / locker order: the name and phone only, Stripe asks for what a
  // card needs besides
  const contact = allPickup(stepData.delivery) ? stepData.billing?.address : null
  if (contact) return { receipt_email: email, payment_method_data: { billing_details: { name: contact.name || email, email, phone: contact.phone || "" } } }
  const address = stepData.billing?.address
  const billing_details = address
    ? {
        name: address.name || email,
        email,
        phone: address.phone || "",
        address: { line1: address.line1, line2: address.line2 || "", city: address.city, state: address.state || "", postal_code: address.postal_code, country: address.country },
      }
    : { email }
  return { receipt_email: email, payment_method_data: { billing_details } }
}

/** Payment Element: never ask again for what our own steps collected */
export function paymentElementOptions() {
  const stepData = getValue("purchase-flow-step-data") || {}
  if (allPickup(stepData.delivery)) return { layout: "tabs", fields: { billingDetails: { name: "never", email: "never", phone: "never" } } }
  const address = stepData.billing?.address
  return {
    layout: "tabs",
    fields: {
      billingDetails: address ? { address: "never", name: "never", email: "never", phone: "never" } : { email: "never" },
    },
  }
}
